import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { studentDocumentsApi } from '@/services/api/endpoints/studentProfile';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { DocumentStatus, DocumentType, StudentDocument } from '@/types/domain';
import { formatDate, humanise } from '@/utils/format';

const DOCUMENT_TYPES: DocumentType[] = [
  'BIRTH_CERTIFICATE',
  'PHOTOGRAPH',
  'PREVIOUS_SCHOOL_RECORD',
  'TRANSFER_CERTIFICATE',
  'MEDICAL_RECORD',
  'IDENTIFICATION',
  'OTHER',
];

const STATUS_TONE: Record<DocumentStatus, 'neutral' | 'success' | 'danger' | 'warning'> = {
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'neutral',
};

/** Bytes, in the units a person reads. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function StudentDocumentsPanel({ studentId }: { studentId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>('BIRTH_CERTIFICATE');
  const [title, setTitle] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [rejecting, setRejecting] = useState<StudentDocument | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [deleting, setDeleting] = useState<StudentDocument | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.studentProfile.documents(studentId),
    queryFn: () => studentDocumentsApi.list(studentId),
    enabled: can(PERMISSIONS.STUDENT_DOCUMENT_VIEW),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });

  const upload = useMutation({
    mutationFn: () => {
      if (file === null) {
        throw new Error('Choose a file first');
      }
      return studentDocumentsApi.upload(studentId, file, {
        documentType,
        title: title === '' ? undefined : title,
        expiryDate: expiryDate === '' ? undefined : expiryDate,
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded');
      setUploadOpen(false);
      setFile(null);
      setTitle('');
      setExpiryDate('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const verify = useMutation({
    mutationFn: ({ id, approved, reason }: { id: string; approved: boolean; reason?: string }) =>
      studentDocumentsApi.verify(studentId, id, approved, reason),
    onSuccess: (updated) => {
      toast.success(`Document ${updated.status.toLowerCase()}`);
      setRejecting(null);
      setRejectionReason('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const remove = useMutation({
    mutationFn: (id: string) => studentDocumentsApi.remove(studentId, id),
    onSuccess: () => {
      toast.success('Document deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setDeleting(null);
    },
  });

  if (!can(PERMISSIONS.STUDENT_DOCUMENT_VIEW)) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Documents"
          description="Certificates and records held for this student."
          action={
            can(PERMISSIONS.STUDENT_DOCUMENT_UPLOAD) ? (
              <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
                Upload
              </Button>
            ) : undefined
          }
        />

        {query.isPending ? (
          <LoadingState label="Loading documents" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">No documents uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {query.data.map((document) => (
              <li key={document.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    {document.title ?? humanise(document.documentType)}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-subtle">
                    {document.fileName} · {formatSize(document.fileSize)}
                    {document.expiryDate !== undefined && ` · expires ${formatDate(document.expiryDate)}`}
                  </p>
                  {document.rejectionReason !== undefined && (
                    <p className="mt-1 text-xs text-danger">Rejected: {document.rejectionReason}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {/* The effective status, so a verified document whose expiry
                      has passed reads as expired rather than still valid. */}
                  <Badge tone={STATUS_TONE[document.effectiveStatus]}>
                    {humanise(document.effectiveStatus)}
                  </Badge>

                  {can(PERMISSIONS.STUDENT_DOCUMENT_VERIFY) && document.status === 'PENDING' && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => verify.mutate({ id: document.id, approved: true })}
                      >
                        Verify
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRejecting(document)}>
                        Reject
                      </Button>
                    </>
                  )}

                  <Button
                    size="sm"
                    variant="ghost"
                    loading={downloading === document.id}
                    onClick={() => {
                      setDownloading(document.id);
                      void studentDocumentsApi
                        .download(studentId, document.id, document.fileName)
                        .catch((error: unknown) => handleApiError(error))
                        .finally(() => setDownloading(null));
                    }}
                  >
                    Download
                  </Button>

                  {can(PERMISSIONS.STUDENT_DOCUMENT_DELETE) && (
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(document)}>
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload a document"
        description="The file is stored under a generated name; the name it arrives with is kept as a label only."
        footer={
          <>
            <Button variant="ghost" onClick={() => setUploadOpen(false)} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={() => upload.mutate()} loading={upload.isPending} disabled={file === null}>
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="document-file" className="mb-1.5 block text-xs font-medium text-muted">
              File<span className="ml-0.5 text-danger">*</span>
            </label>
            <input
              id="document-file"
              ref={fileInput}
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
            />
          </div>

          <SelectField
            label="Document type"
            required
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value as DocumentType)}
          >
            {DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Optional label"
          />

          <TextField
            label="Expiry date"
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            hint="When set, the document stops reading as verified once this date passes"
          />
        </div>
      </Modal>

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Reject this document"
        description="The reason is shown to whoever uploaded it, so they know what to fix."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={verify.isPending}
              disabled={rejectionReason.trim() === ''}
              onClick={() => {
                if (rejecting !== null) {
                  verify.mutate({ id: rejecting.id, approved: false, reason: rejectionReason });
                }
              }}
            >
              Reject
            </Button>
          </>
        }
      >
        <TextField
          label="Reason"
          required
          value={rejectionReason}
          onChange={(event) => setRejectionReason(event.target.value)}
          placeholder="e.g. The scan is unreadable"
        />
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title="Delete this document?"
        message={`${deleting?.fileName ?? ''} will be removed from storage. This cannot be undone.`}
        confirmLabel="Delete"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id);
          }
        }}
      />
    </>
  );
}
